import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import type { Amenity, AvailabilitySchedule, UpdateAmenityRequest } from '@/types/v2/amenity';
import { updateAmenity } from '@/api/amenities';
import { ProfessionalDialog } from '@/components/common';
import { toast } from '@/components/core/v2/toaster';
import { useUser } from '@/hooks/v2/use-user';
import { useConnectStatus } from '@/hooks/v2/use-connect-status';

import { AmenityForm, buildEditDefaults, DAYS_OF_WEEK } from './amenity-form';
import type { AmenityFormValues } from './amenity-form';
import { amenitySchema } from './amenity-schema';

interface EditAmenityDialogProps {
  open: boolean;
  amenity: Amenity;
  /** When provided (e.g. from detail page URL), used for API calls. Falls back to amenity.uuid ?? amenity.id */
  amenityId?: string;
  onClose: () => void;
  onSuccess: (amenity: Amenity) => void;
}

export function EditAmenityDialog({ open, amenity, amenityId: amenityIdProp, onClose, onSuccess }: EditAmenityDialogProps): React.JSX.Element {
  const { community } = useUser();
  const { isReady: stripeReady, paymentsEnabled } = useConnectStatus(community?.id);
  const queryClient = useQueryClient();

  const resolvedAmenityId = amenityIdProp ?? amenity?.uuid ?? (amenity?.id != null ? String(amenity.id) : '');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AmenityFormValues>({
    resolver: zodResolver(amenitySchema),
    defaultValues: buildEditDefaults(amenity),
  });

  // Re-populate whenever the dialog opens with a (possibly different) amenity
  React.useEffect(() => {
    if (open && amenity) {
      reset(buildEditDefaults(amenity));
    }
  }, [open, amenity, reset]);

  const mutation = useMutation({
    mutationFn: (data: Partial<UpdateAmenityRequest>) =>
      updateAmenity(community?.id || '', resolvedAmenityId, data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['amenities', community?.id] });
      queryClient.invalidateQueries({ queryKey: ['amenity', resolvedAmenityId] });
      toast.success('Amenity updated');
      onSuccess(updated);
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to update amenity');
    },
  });

  const onSubmit = (data: AmenityFormValues) => {
    const availability_schedule: AvailabilitySchedule = {};
    for (const day of DAYS_OF_WEEK) {
      const entry = data.availability[day];
      if (entry?.enabled) {
        availability_schedule[day] = { start: entry.start, end: entry.end };
      }
    }

    const required_documents = (data.required_documents ?? []).map((s) => s.trim()).filter(Boolean);

    mutation.mutate({
      name: data.name,
      description: data.description,
      rules: data.rules,
      fee_description: data.fee_description,
      deposit_amount: data.deposit_amount,
      total_fee_amount: data.total_fee_amount,
      required_documents: required_documents.length > 0 ? required_documents : undefined,
      availability_schedule: Object.keys(availability_schedule).length > 0 ? availability_schedule : null,
      seasonal_start_date: data.seasonal_enabled ? data.seasonal_start_date : null,
      seasonal_end_date: data.seasonal_enabled ? data.seasonal_end_date : null,
      time_block_duration: data.time_block_duration,
      reservation_min_duration: data.reservation_min_duration ?? null,
      reservation_max_duration: data.reservation_max_duration ?? null,
      max_guests: data.max_guests ?? null,
      guest_count_required: data.guest_count_required,
      alcohol_question_enabled: data.alcohol_question_enabled,
      requires_approval: data.requires_approval,
      is_active: data.is_active,
    });
  };

  return (
    <ProfessionalDialog
      open={open}
      onClose={onClose}
      title="Edit Amenity"
      subtitle={amenity?.name ?? 'Edit amenity'}
      maxWidth="md"
      actions={
        <>
          <Button onClick={onClose} variant="outlined" disabled={mutation.isPending}>
            Cancel
          </Button>
          <LoadingButton
            loading={mutation.isPending || isSubmitting}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
          >
            Save Changes
          </LoadingButton>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <AmenityForm control={control} errors={errors} mode="edit" paymentsEnabled={paymentsEnabled} stripeReady={stripeReady} />
      </form>
    </ProfessionalDialog>
  );
}
